import axiosInstance from './axiosInstance';

/** Build a FormData from `payload` when any of `fileKeys` carries a File
 *  (caller swaps to multipart). Returns null when no file is present so
 *  the caller can default to a JSON post. Skips undefined values. */
function _maybeFormData(payload, fileKeys) {
  const hasFile = fileKeys.some(k => payload[k] instanceof File);
  if (!hasFile) return null;
  const fd = new FormData();
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined) continue;
    if (fileKeys.includes(k) && !(v instanceof File)) continue;
    // Plain objects (e.g. translations JSON) must be JSON-encoded ·
    // FormData would otherwise call toString() and send "[object Object]".
    if (v !== null && typeof v === 'object' && !(v instanceof File) && !(v instanceof Blob)) {
      fd.append(k, JSON.stringify(v));
      continue;
    }
    fd.append(k, v);
  }
  return fd;
}

/**
 * Resources can carry a file alongside the regular fields. When the caller
 * passes payload.file as a File object we switch the request to multipart;
 * payload.file === null clears an existing file (sent as empty string ·
 * DRF maps that back to a falsy FileField and our serializer clears it);
 * payload.file === undefined leaves the file alone (we strip the key).
 */
function _resourceSave(method, url, payload) {
  const hasFileMutation = Object.prototype.hasOwnProperty.call(payload, 'file');
  const fileVal = payload.file;
  const isFileUpload = fileVal instanceof File;
  if (hasFileMutation) {
    const fd = new FormData();
    for (const [k, v] of Object.entries(payload)) {
      if (k === 'file') continue;
      if (v === undefined || v === null) continue;
      fd.append(k, v);
    }
    if (isFileUpload) {
      fd.append('file', fileVal);
    } else if (fileVal === null) {
      // Clearing: an empty string tells DRF to set file to falsy.
      fd.append('file', '');
    }
    return axiosInstance[method](url, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  }
  const { file: _omit, ...rest } = payload;  // eslint-disable-line no-unused-vars
  return axiosInstance[method](url, rest).then(r => r.data);
}

/* FormData from a plain object · files go in as files, everything else
 * as a string, null / undefined are skipped. */
const _multipart = (payload) => {
  const fd = new FormData();
  Object.entries(payload || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, v);
  });
  return fd;
};

const api = {
  me:               () => axiosInstance.get('api/users/me/').then(r => r.data),
  myEmployee:       () => axiosInstance.get('api/employees/me/').then(r => r.status === 204 ? null : r.data),
  updateMyFavorites:(slugs) => axiosInstance.patch('api/employees/me/', { favorite_apps: slugs }).then(r => r.data),
  updateMyLocale:   (locale) => axiosInstance.patch('api/employees/me/', { preferred_locale: locale }).then(r => r.data),
  uploadMyPhoto:    (file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return axiosInstance.patch('api/employees/me/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  clearMyPhoto:     () => axiosInstance.patch('api/employees/me/', { photo: null }).then(r => r.data),
  salesKpis:        () => axiosInstance.get('api/kpis/sales/').then(r => r.data),
  expenseWorkflow:  () => axiosInstance.get('api/expenses/workflow/').then(r => r.data),
  storageProbe:     () => axiosInstance.post('api/admin/storage-probe/').then(r => r.data),
  appAuthProbe:     () => axiosInstance.post('api/admin/app-auth-probe/').then(r => r.data),
  apps:             () => axiosInstance.get('api/apps/').then(r => r.data),
  allApps:          () => axiosInstance.get('api/apps/', { params: { all: 1 } }).then(r => r.data),
  app:              (slug) => axiosInstance.get(`api/apps/${slug}/`).then(r => r.data),
  myAppAccessRequests: () => axiosInstance.get('api/apps/my-requests/').then(r => r.data),
  requestAppAccess:    (slug, message = '') => axiosInstance.post(`api/apps/${slug}/request-access/`, { message }).then(r => r.data),
  adminAppAccessRequests: () => axiosInstance.get('api/admin/app-access-requests/').then(r => r.data),
  adminDecideAppAccessRequest: (id, decision, comment = '') => axiosInstance.post(`api/admin/app-access-requests/${id}/decide/`, { decision, comment }).then(r => r.data),
  adminApps:        () => axiosInstance.get('api/admin/apps/').then(r => r.data),
  adminCreateApp:   (payload) => axiosInstance.post('api/admin/apps/', payload).then(r => r.data),
  adminAppModules:  (id) => axiosInstance.get(`api/admin/apps/${id}/modules/`).then(r => r.data),
  adminUpdateApp:   (id, payload) => axiosInstance.patch(`api/admin/apps/${id}/`, payload).then(r => r.data),
  adminUploadAppLogo: (id, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return axiosInstance.patch(`api/admin/apps/${id}/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  adminClearAppLogo: (id) => axiosInstance.patch(`api/admin/apps/${id}/`, { logo: null }).then(r => r.data),
  adminDeleteApp:   (id) => axiosInstance.delete(`api/admin/apps/${id}/`),
  employees:        () => axiosInstance.get('api/employees/').then(r => r.data),
  createEmployee:   (payload) => axiosInstance.post('api/employees/', payload).then(r => r.data),
  updateEmployee:   (id, payload) => axiosInstance.patch(`api/employees/${id}/`, payload).then(r => r.data),
  deleteEmployee:   (id) => axiosInstance.delete(`api/employees/${id}/`),
  myBankAccounts:    () => axiosInstance.get('api/employees/me/bank-accounts/').then(r => r.data),
  createBankAccount: (payload) => axiosInstance.post('api/employees/me/bank-accounts/', payload).then(r => r.data),
  updateBankAccount: (id, payload) => axiosInstance.patch(`api/employees/me/bank-accounts/${id}/`, payload).then(r => r.data),
  deleteBankAccount: (id) => axiosInstance.delete(`api/employees/me/bank-accounts/${id}/`),
  expenses:         () => axiosInstance.get('api/expenses/').then(r => r.data),
  expense:    (id)  => axiosInstance.get(`api/expenses/${id}/`).then(r => r.data),
  createExpense: (payload) => axiosInstance.post('api/expenses/', payload).then(r => r.data),
  updateExpense: (id, payload) => axiosInstance.patch(`api/expenses/${id}/`, payload).then(r => r.data),
  deleteExpense: (id) => axiosInstance.delete(`api/expenses/${id}/`),
  submitExpense: (id, locale, bankAccountId) => axiosInstance.post(`api/expenses/${id}/submit/`, { locale, bank_account_id: bankAccountId }).then(r => r.data),
  toReview:        () => axiosInstance.get('api/expenses/to-review/').then(r => r.data),
  approveExpense: (id, comment) => axiosInstance.post(`api/expenses/${id}/approve/`, { comment }).then(r => r.data),
  rejectExpense:  (id, comment) => axiosInstance.post(`api/expenses/${id}/reject/`,  { comment }).then(r => r.data),
  combinedPdf:    (id) => axiosInstance.get(`api/expenses/${id}/combined-pdf/`, { responseType: 'blob' }).then(r => r.data),

  departments:        () => axiosInstance.get('api/departments/').then(r => r.data),
  adminMe:            () => axiosInstance.get('api/admin/me/').then(r => r.data),
  adminCreateDepartment: (payload) => axiosInstance.post('api/admin/departments/', payload).then(r => r.data),
  adminUpdateDepartment: (id, payload) => axiosInstance.patch(`api/admin/departments/${id}/`, payload).then(r => r.data),
  adminDeleteDepartment: (id) => axiosInstance.delete(`api/admin/departments/${id}/`),
  adminEmployees:     () => axiosInstance.get('api/admin/employees/').then(r => r.data),
  employeeActivity:   () => axiosInstance.get('api/admin/employee-activity/').then(r => r.data),
  adminCreateEmployee:(payload) => axiosInstance.post('api/admin/employees/', payload).then(r => r.data),
  adminUpdateEmployee:(id, payload) => axiosInstance.patch(`api/admin/employees/${id}/`, payload).then(r => r.data),
  adminAddBankAccount:(id, payload) => axiosInstance.post(`api/admin/employees/${id}/bank-accounts/`, payload).then(r => r.data),
  adminUpdateBankAccount:(id, accId, payload) => axiosInstance.patch(`api/admin/employees/${id}/bank-accounts/${accId}/`, payload).then(r => r.data),
  adminDeleteBankAccount:(id, accId) => axiosInstance.delete(`api/admin/employees/${id}/bank-accounts/${accId}/`),
  adminExpenseReports:(statusFilter) => axiosInstance.get('api/admin/expense-reports/', { params: statusFilter ? { status: statusFilter } : {} }).then(r => r.data),

  announcements: (params) => axiosInstance.get('api/announcements/', { params }).then(r => r.data),
  createAnnouncement: (payload) => axiosInstance.post('api/announcements/', payload).then(r => r.data),
  updateAnnouncement: (id, payload) => axiosInstance.patch(`api/announcements/${id}/`, payload).then(r => r.data),
  deleteAnnouncement: (id) => axiosInstance.delete(`api/announcements/${id}/`),
  uploadAnnouncementAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosInstance.post(`api/announcements/${id}/attachments/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteAnnouncementAttachment: (id, attId) =>
    axiosInstance.delete(`api/announcements/${id}/attachments/${attId}/`),
  toggleReaction: (id, emoji) =>
    axiosInstance.post(`api/announcements/${id}/reactions/`, { emoji }).then(r => r.data),
  postComment: (id, body) =>
    axiosInstance.post(`api/announcements/${id}/comments/`, { body }).then(r => r.data),
  deleteComment: (id, commentId) =>
    axiosInstance.delete(`api/announcements/${id}/comments/${commentId}/`),

  events: ({ start, end, departement } = {}) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end)   params.set('end', end);
    if (departement) params.set('departement', departement);
    return axiosInstance.get(`api/events/?${params.toString()}`).then(r => r.data);
  },
  createEvent: (payload) => axiosInstance.post('api/events/', payload).then(r => r.data),
  updateEvent: (id, payload) => axiosInstance.patch(`api/events/${id}/`, payload).then(r => r.data),
  deleteEvent: (id) => axiosInstance.delete(`api/events/${id}/`),

  googleCalendarStatus:   () => axiosInstance.get('api/google-calendar/status/').then(r => r.data),
  googleCalendarOAuthUrl: () => axiosInstance.get('api/google-calendar/oauth-url/').then(r => r.data),
  googleCalendarOAuthCallback: (code, state) =>
    axiosInstance.post('api/google-calendar/oauth-callback/', { code, state }).then(r => r.data),
  googleCalendarSync:     () => axiosInstance.post('api/google-calendar/sync/').then(r => r.data),
  isHR: () => axiosInstance.get('api/announcements/me/is-hr/').then(r => r.data.is_hr),
  /* MANEVA CMS gate · HR, app admins, and anyone flagged as a content
   * editor on their Employee row can read/write. Falls back to false
   * if the endpoint isn't reachable. */
  canEditContent: () => axiosInstance.get('api/announcements/me/is-hr/').then(
    r => !!(r.data.is_hr || r.data.is_content_editor),
  ),

  resources: () => axiosInstance.get('api/resources/').then(r => r.data),
  createResource: (payload) => _resourceSave('post', 'api/resources/', payload),
  updateResource: (id, payload) => _resourceSave('patch', `api/resources/${id}/`, payload),
  deleteResource: (id) => axiosInstance.delete(`api/resources/${id}/`),

  newsletterPhotos: (params) => axiosInstance.get('api/newsletter-photos/', { params }).then(r => r.data),
  newsletterAlbums:    () => axiosInstance.get('api/newsletter-albums/').then(r => r.data),
  adminCreateAlbum:    (payload) => axiosInstance.post('api/newsletter-albums/', payload).then(r => r.data),
  adminUpdateAlbum:    (id, payload) => axiosInstance.patch(`api/newsletter-albums/${id}/`, payload).then(r => r.data),
  adminDeleteAlbum:    (id) => axiosInstance.delete(`api/newsletter-albums/${id}/`),
  adminCreatePhoto: (payload) => {
    const fd = _maybeFormData(payload, ['image']);
    if (fd) return axiosInstance.post('api/newsletter-photos/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.post('api/newsletter-photos/', payload).then(r => r.data);
  },
  adminUpdatePhoto: (id, payload) => {
    const fd = _maybeFormData(payload, ['image']);
    if (fd) return axiosInstance.patch(`api/newsletter-photos/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.patch(`api/newsletter-photos/${id}/`, payload).then(r => r.data);
  },
  adminDeletePhoto: (id) => axiosInstance.delete(`api/newsletter-photos/${id}/`),

  // ── Newsletter events (community / homepage) ─────────────────────
  newsletterEvents:       (params) => axiosInstance.get('api/newsletter-events/', { params }).then(r => r.data),
  // Mosy's marketing calendar, read-only (current + next six weeks).
  marketingCampaigns:     () => axiosInstance.get('api/marketing/campaigns/upcoming/').then(r => r.data),
  adminCreateEvent:       (payload) => {
    const fd = _maybeFormData(payload, ['image']);
    if (fd) return axiosInstance.post('api/newsletter-events/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.post('api/newsletter-events/', payload).then(r => r.data);
  },
  adminUpdateEvent:       (id, payload) => {
    const fd = _maybeFormData(payload, ['image']);
    if (fd) return axiosInstance.patch(`api/newsletter-events/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.patch(`api/newsletter-events/${id}/`, payload).then(r => r.data);
  },
  adminDeleteEvent:       (id) => axiosInstance.delete(`api/newsletter-events/${id}/`),

  // ── Newsletter spotlight · the clip on the newsletter home ───────
  newsletterSpotlight:  (params) => axiosInstance.get('api/newsletter-spotlight/', { params }).then(r => r.data),
  adminCreateSpotlight: (payload) => axiosInstance.post('api/newsletter-spotlight/', _multipart(payload),
                                        { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  adminUpdateSpotlight: (id, payload) => axiosInstance.patch(`api/newsletter-spotlight/${id}/`, _multipart(payload),
                                        { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  adminDeleteSpotlight: (id) => axiosInstance.delete(`api/newsletter-spotlight/${id}/`),

  // ── Newsletter CMS (read · authenticated employees) ──────────────
  newsletters:        (params) => axiosInstance.get('api/newsletters/', { params }).then(r => r.data),
  newsletter:   (slug) => axiosInstance.get(`api/newsletters/${slug}/`).then(r => r.data),
  // Fire-and-forget · the reader opened an episode. Swallows errors
  // (server hiccup shouldn't disturb the read experience).
  // the episode as a shareable PDF · a few seconds when it has many photos
  newsletterPdf: (slug, locale) =>
    axiosInstance.get(`api/newsletters/${slug}/pdf/`, { params: { locale }, responseType: 'blob' })
      .then(r => r.data),
  recordNewsletterView: (slug, locale) =>
    axiosInstance.post(`api/newsletters/${slug}/view/`, { locale }).catch(() => null),

  // ── Newsletter CMS (admin · HR write) ────────────────────────────
  adminNewsletterAudienceSize: () =>
    axiosInstance.get('api/admin/newsletters/audience-size/').then(r => r.data),
  adminNewsletterReaders: (id) =>
    axiosInstance.get(`api/admin/newsletters/${id}/readers/`).then(r => r.data),
  adminNewsletters:       () => axiosInstance.get('api/admin/newsletters/').then(r => r.data),
  adminNewsletter:   (id) => axiosInstance.get(`api/admin/newsletters/${id}/`).then(r => r.data),
  adminCreateNewsletter:  (payload) => {
    const fd = _maybeFormData(payload, ['cover']);
    if (fd) return axiosInstance.post('api/admin/newsletters/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.post('api/admin/newsletters/', payload).then(r => r.data);
  },
  adminUpdateNewsletter:  (id, payload) => {
    const fd = _maybeFormData(payload, ['cover']);
    if (fd) return axiosInstance.patch(`api/admin/newsletters/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.patch(`api/admin/newsletters/${id}/`, payload).then(r => r.data);
  },
  adminDeleteNewsletter:  (id) => axiosInstance.delete(`api/admin/newsletters/${id}/`),
  // the publish action · status + publication moment + optional Slack post
  adminPublishNewsletter:   (id, announceSlack) =>
    axiosInstance.post(`api/admin/newsletters/${id}/publish/`, { announce_slack: !!announceSlack }).then(r => r.data),
  adminUnpublishNewsletter: (id) => axiosInstance.post(`api/admin/newsletters/${id}/unpublish/`).then(r => r.data),
  adminCreateSection:     (newsletterId, payload) => axiosInstance.post(`api/admin/newsletters/${newsletterId}/sections/`, payload).then(r => r.data),
  adminUpdateSection:     (id, payload) => axiosInstance.patch(`api/admin/newsletter-sections/${id}/`, payload).then(r => r.data),
  adminDeleteSection:     (id) => axiosInstance.delete(`api/admin/newsletter-sections/${id}/`),
  adminDuplicateSection:  (id) => axiosInstance.post(`api/admin/newsletter-sections/${id}/duplicate/`).then(r => r.data),
  adminReorderSections:   (newsletterId, ids) => axiosInstance.post(`api/admin/newsletters/${newsletterId}/sections/reorder/`, { ids }).then(r => r.data),
  adminUploadSectionImage:(sectionId, file, caption = '') => {
    const fd = new FormData();
    fd.append('image', file);
    if (caption) fd.append('caption', caption);
    return axiosInstance.post(`api/admin/newsletter-sections/${sectionId}/images/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  adminDeleteSectionImage:(id) => axiosInstance.delete(`api/admin/newsletter-section-images/${id}/`),
  adminTranslate: (source_lang, target_lang, fields) =>
    axiosInstance.post('api/admin/newsletters/translate/', { source_lang, target_lang, fields }).then(r => r.data),

  // ── Job postings (careers · jobs.lamptwist.be) ───────────────────
  // Organizations (companies a job belongs to · LampTwist, Calum, …).
  adminOrganizations:      () => axiosInstance.get('api/admin/organizations/').then(r => r.data),
  adminOrganization:  (id) => axiosInstance.get(`api/admin/organizations/${id}/`).then(r => r.data),
  adminCreateOrganization: (payload) => {
    const fd = _maybeFormData(payload, ['logo']);
    if (fd) return axiosInstance.post('api/admin/organizations/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.post('api/admin/organizations/', payload).then(r => r.data);
  },
  adminUpdateOrganization: (id, payload) => {
    const fd = _maybeFormData(payload, ['logo']);
    if (fd) return axiosInstance.patch(`api/admin/organizations/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.patch(`api/admin/organizations/${id}/`, payload).then(r => r.data);
  },
  adminDeleteOrganization: (id) => axiosInstance.delete(`api/admin/organizations/${id}/`),

  // Job posts (drafts + published).
  adminJobs:        () => axiosInstance.get('api/admin/jobs/').then(r => r.data),
  adminJob:    (id) => axiosInstance.get(`api/admin/jobs/${id}/`).then(r => r.data),
  adminCreateJob:   (payload) => {
    const fd = _maybeFormData(payload, ['cover']);
    if (fd) return axiosInstance.post('api/admin/jobs/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.post('api/admin/jobs/', payload).then(r => r.data);
  },
  adminUpdateJob:   (id, payload) => {
    const fd = _maybeFormData(payload, ['cover']);
    if (fd) return axiosInstance.patch(`api/admin/jobs/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    return axiosInstance.patch(`api/admin/jobs/${id}/`, payload).then(r => r.data);
  },
  adminDeleteJob:   (id) => axiosInstance.delete(`api/admin/jobs/${id}/`),

  // ── Internal surveys ─────────────────────────────────────────────
  surveyMe:      (slug) => axiosInstance.get(`api/surveys/${slug}/me/`).then(r => r.data),
  saveSurvey:    (slug, answers, locale) =>
    axiosInstance.put(`api/surveys/${slug}/me/`, { answers, locale }).then(r => r.data),
  surveyResults: (slug) => axiosInstance.get(`api/admin/surveys/${slug}/results/`).then(r => r.data),

  // ── Career map (HR · unpublished maps are HR / admin only) ────────
  careerMaps:        () => axiosInstance.get('api/career-maps/').then(r => r.data),
  careerMapFile:     (slug) => axiosInstance.get(`api/career-maps/${slug}/file/`, { responseType: 'blob' }),
  createCareerMap:   ({ title, file }) => {
    if (!file) return axiosInstance.post('api/admin/career-maps/', { title }).then(r => r.data);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    return axiosInstance.post('api/admin/career-maps/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  attachCareerMap:   (slug, file, replaceContent) => {
    const fd = new FormData();
    fd.append('file', file);
    if (replaceContent) fd.append('import', '1');
    return axiosInstance.post(`api/admin/career-maps/${slug}/attachment/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  updateCareerMap:   (slug, payload) => axiosInstance.patch(`api/admin/career-maps/${slug}/`, payload).then(r => r.data),
  deleteCareerMap:   (slug) => axiosInstance.delete(`api/admin/career-maps/${slug}/`),
  careerMapDocument:  (id) => axiosInstance.get(`api/career-maps/documents/${id}/file/`, { responseType: 'blob' }),
  addCareerMapDocument: (file, title) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    return axiosInstance.post('api/admin/career-maps/documents/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteCareerMapDocument: (id) => axiosInstance.delete(`api/admin/career-maps/documents/${id}/`),
  translateCareerMap: (slug, locale, reset) =>
    axiosInstance.post(`api/admin/career-maps/${slug}/translate/`, { locale, reset: !!reset }, { timeout: 150000 }).then(r => r.data),
  uploadAttachment: (id, file, kind = 'receipt', lineId = null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    if (lineId) fd.append('line', lineId);
    return axiosInstance.post(`api/expenses/${id}/attachments/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  updateAttachment: (id, attId, payload) =>
    axiosInstance.patch(`api/expenses/${id}/attachments/${attId}/`, payload).then(r => r.data),
  deleteAttachment: (id, attId) =>
    axiosInstance.delete(`api/expenses/${id}/attachments/${attId}/`),
};

export default api;
